// CSV export helpers for admin-api downloads (`responseType: 'blob'`). Pure; unit-tested.

/**
 * With `responseType: 'blob'` axios hands error bodies over as a Blob, so `code` (e.g.
 * MFA_REQUIRED) is invisible to error views and the step-up interceptor. Returns the parsed JSON
 * body for a Blob error, `{ error: text }` for a non-JSON one, and `data` unchanged otherwise.
 */
export async function blobErrorBodyToJson(data: unknown): Promise<unknown> {
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return data;
  let text: string;
  try {
    text = await data.text();
  } catch {
    return data;
  }
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return { error: trimmed.length > 400 ? `${trimmed.slice(0, 397)}...` : trimmed };
  }
}

/** Data records in a CSV text (quote-aware, header row excluded, trailing newline ignored). */
export function csvDataRowCount(text: string): number {
  let records = 0;
  let inQuotes = false;
  let hasContent = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      hasContent = true;
    } else if (ch === '\n' && !inQuotes) {
      if (hasContent) records += 1;
      hasContent = false;
    } else if (ch !== '\r') {
      hasContent = true;
    }
  }
  if (hasContent) records += 1;
  return Math.max(0, records - 1);
}

export type CsvExportResult = {
  /** The server hit its export row cap (`X-Truncated: true`). */
  truncated: boolean;
  /** Data rows in the file. */
  rows: number;
};

/** `X-Truncated` header (axios lower-cases names) → boolean. */
export function isExportTruncated(headers: Record<string, unknown> | null | undefined): boolean {
  const raw = headers?.['x-truncated'] ?? headers?.['X-Truncated'];
  return String(raw ?? '').trim().toLowerCase() === 'true';
}

/** Warning shown when an export was truncated. */
export function exportTruncatedMessage(result: CsvExportResult | null | undefined): string | null {
  if (!result?.truncated) return null;
  return `Export truncated at ${result.rows.toLocaleString('en-US')} rows — narrow the filters.`;
}
